/**
 * Environment configuration
 */

import { VERSION } from "./version";

export const ENV = {
  // Application
  APP_NAME: "Stagecraft",
  APP_VERSION: VERSION,

  // Development
  DEV_MODE: import.meta.env.DEV,
  PROD_MODE: import.meta.env.PROD,

  // Units and Scale
  UNITS: {
    // Base unit definition (1 internal unit = 1 millimeter)
    BASE_UNIT_IN_MM: 1,

    // Common conversion factors (from millimeters)
    CONVERSIONS: {
      MM_PER_UNIT: 1, // 1 unit = 1mm (identity)
      CM_PER_UNIT: 0.1, // 1 unit = 0.1cm
      M_PER_UNIT: 0.001, // 1 unit = 0.001m
      INCH_PER_UNIT: 0.0393701, // 1 unit = 0.0393701 inches
      FEET_PER_UNIT: 0.00328084, // 1 unit = 0.00328084 feet
    },

    // Grid intervals in base units (millimeters)
    MAJOR_INTERVAL_MM: 1000, // Major grid lines every 1000mm (1m)
    MINOR_INTERVAL_MM: 100, // Minor grid lines every 100mm (10cm)
  },

  // Coordinate System (CAD Convention)
  COORDINATE_SYSTEM: {
    // Axis definitions
    UP_VECTOR: [0, 0, 1] as [number, number, number], // Z-up
    RIGHT_VECTOR: [1, 0, 0] as [number, number, number], // X-right
    FORWARD_VECTOR: [0, -1, 0] as [number, number, number], // -Y forward

    // Default object positions (in base units - millimeters)
    CUBE_POSITION: {
      X: 250, // 250mm (25cm) right
      Y: -450, // 450mm (45cm) back
      Z: 100, // 100mm (10cm) up
    },

    // Pivot point offsets (in base units)
    PIVOT_OFFSETS: {
      BOTTOM_CENTER: [0, 0, 5] as [number, number, number], // 5mm (0.5cm) up from base
    },
  },

  // Camera
  CAMERA: {
    FOV: 45,
    NEAR: 0.1,
    FAR: 100000, // 100000mm (100m) in base units
    // Position for standard CAD view (in base units)
    DEFAULT_POSITION: [500, -500, 300] as [number, number, number], // 500mm right, 500mm back, 300mm up
    DEFAULT_TARGET: [250, -450, 100] as [number, number, number], // Look at cube position
    DEFAULT_UP: [0, 0, 1] as [number, number, number], // Z-up

    // Controls
    ORBIT_SPEED: 1.0,
    PAN_SPEED: 1.0,
    ZOOM_SPEED: 1.0,
    MIN_ZOOM_DISTANCE: 0.0001, // Effectively no minimum zoom
    MAX_ZOOM_DISTANCE: Number.MAX_SAFE_INTEGER, // No maximum zoom limit
  },

  // Grid
  GRID: {
    ENABLED: true,
    SIZE: 100000, // 100000mm (100m) in base units
    MAJOR_SPACING: 1000, // 1000mm (1m) in base units
    MINOR_DIVISIONS: 10, // 100mm (10cm) subdivisions
    MAJOR_COLOR: [0.8, 0.8, 0.8] as [number, number, number],
    MINOR_COLOR: [0.2, 0.2, 0.2] as [number, number, number],
    MAJOR_OPACITY: 0.5,
    MINOR_OPACITY: 0.2,
    FADE_DISTANCE: 50000, // 50000mm (50m) fade distance in base units
    // Standard CAD colors for axes
    AXIS_COLORS: {
      X: [1.0, 0.0, 0.0] as [number, number, number], // Pure red for X
      Y: [0.0, 1.0, 0.0] as [number, number, number], // Green #00FF00
      Z: [0.0, 0.0, 1.0] as [number, number, number], // Pure blue for Z
    },
    SHOW_AXES: true,
    AXES_SIZE: 5000, // 5000mm (5m) in base units
  },

  // Debug
  DEBUG: {
    ENABLED: import.meta.env.DEV,
    LOG_LEVEL: import.meta.env.DEV ? "debug" : "error",
  },

  // Performance
  PERFORMANCE: {
    TARGET_FPS: 60,
    ENABLE_ADAPTIVE_QUALITY: true,
    LOD_BIAS: 0,
    MAX_VISIBLE_OBJECTS: 10000,
    // WebGL specific settings
    ANTIALIAS: true,
    SHADOW_MAP_SIZE: 2048,
    MAX_LIGHTS: 4,
    MAX_TEXTURE_SIZE: 4096,
  },
} as const;

// Type for environment configuration
export type Environment = typeof ENV;

// Validate environment at runtime
function validateEnvironment() {
  if (ENV.DEBUG.ENABLED) {
    // console.log('Environment configuration:', ENV); // Removed for production

    if (ENV.CAMERA.NEAR >= ENV.CAMERA.FAR) {
      // console.warn('Camera NEAR plane should be less than FAR plane'); // Removed for production
    }

    // Validate unit conversion consistency
    const mmToM =
      ENV.UNITS.CONVERSIONS.MM_PER_UNIT * ENV.UNITS.CONVERSIONS.M_PER_UNIT;
    if (Math.abs(mmToM - 0.001) > Number.EPSILON) {
      // console.warn('Unit conversion inconsistency detected in MM to M conversion'); // Removed for production
    }
  }
}

// Run validation in development
if (ENV.DEV_MODE) {
  validateEnvironment();
}

export default ENV;
